// Presentational parts (chips, cells, banner, header) for the
// Requirement Intelligence drawer.
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileCheck,
  Info,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import type { RequirementFinding } from "../api/contentRegistryApi";
import {
  DOC_TYPE_COLORS,
  DOC_TYPE_FALLBACK_BADGE_CLS,
  EMPTY_PLACEHOLDER,
  REQUIREMENT_STATUS_DOT_CLS,
  REQUIREMENT_STATUS_LABEL,
  REQUIREMENT_STATUS_PILL_CLS,
  REQUIREMENT_STRENGTH_PILL_CLS,
  type RequirementStatusKind,
  type RequirementStrengthKind,
} from "../constants";
import { formatDocType, plainText } from "../utils";
import {
  DECOMP_LABELS,
  HEADER_ICON_BOX_CLS,
  HEADER_ICON_CLS,
  META_CHIP_CLS,
  META_CHIP_FOREGROUND_CLS,
  REQ_ID_CHIP_CLS,
  REQUIREMENT_DRAWER_COPY,
  ROW_TOGGLE_CLS,
  STATUS_BANNER_OK_CLS,
  STATUS_BANNER_WARN_CLS,
  formatConstraints,
  metaForModal,
  strengthKindFromFinding,
  titleCase,
} from "./utilities";

function DocTypeBadge({ documentType }: { documentType: string }) {
  const key = String(plainText(documentType)).toUpperCase();
  const toneCls = DOC_TYPE_COLORS[key] ?? DOC_TYPE_FALLBACK_BADGE_CLS;
  return (
    <span
      className={`inline-flex items-center justify-center px-2 py-0.5 rounded-[4px] text-[10px] font-bold ${toneCls}`}
    >
      {formatDocType(key)}
    </span>
  );
}

export function ReqIdChip({ id }: { id: string }) {
  return (
    <span title={id} className={REQ_ID_CHIP_CLS}>
      {id}
    </span>
  );
}

export function StrengthPill({
  kind,
  label,
}: {
  kind: RequirementStrengthKind;
  label: string;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${REQUIREMENT_STRENGTH_PILL_CLS[kind]}`}
    >
      {label}
    </span>
  );
}

export function EmptyRow({ text }: { text: string }) {
  return (
    <div className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
      <Info className="size-4" />
      {text}
    </div>
  );
}

function DecompCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-[12px] text-foreground break-words">
        {value || EMPTY_PLACEHOLDER}
      </div>
    </div>
  );
}

function resolveReqId(finding: RequirementFinding): string {
  const raw =
    finding.requirement_id?.trim() ||
    finding.id?.trim() ||
    finding.clause_id?.trim() ||
    "";
  return raw ? plainText(raw) : "REQ";
}

function resolvePageValue(finding: RequirementFinding): string {
  if (finding.page === null || finding.page === undefined) return "";
  return String(finding.page);
}

export function RequirementRow({
  finding,
  expanded,
  onToggle,
}: {
  finding: RequirementFinding;
  expanded: boolean;
  onToggle: () => void;
}) {
  const reqId = resolveReqId(finding);
  const text = plainText(finding.text || "");
  const kind = strengthKindFromFinding(finding);
  const modal = (finding.modal || "").trim().toLowerCase();
  const meta = metaForModal(modal);
  const strengthLabel = meta.label.replace(/\s*\(.*\)$/, "");
  const constraints = formatConstraints(finding.constraints);
  const pageValue = resolvePageValue(finding);
  const sectionValue = finding.section ? `§${plainText(finding.section)}` : "";

  return (
    <li className="rounded-lg border border-border bg-card/60">
      <Button
        type="button"
        variant="ghost"
        size="none"
        onClick={onToggle}
        className={ROW_TOGGLE_CLS}
        aria-expanded={expanded}
      >
        <ReqIdChip id={reqId} />
        <p
          className="flex-1 min-w-0 text-[12.5px] leading-relaxed text-foreground break-words line-clamp-2"
          title={text}
        >
          {text || EMPTY_PLACEHOLDER}
        </p>
        <StrengthPill kind={kind} label={strengthLabel || titleCase(kind)} />
        {expanded ? (
          <ChevronDown className="size-4 text-muted-foreground shrink-0 mt-0.5" />
        ) : (
          <ChevronRight className="size-4 text-muted-foreground shrink-0 mt-0.5" />
        )}
      </Button>
      {expanded && (
        <div className="border-t border-border/60 grid grid-cols-1 gap-2 p-3 sm:grid-cols-2">
          <DecompCell label={DECOMP_LABELS.actor} value={plainText(finding.actor || "")} />
          <DecompCell label={DECOMP_LABELS.action} value={plainText(finding.action || "")} />
          <DecompCell label={DECOMP_LABELS.object} value={plainText(finding.object || "")} />
          <DecompCell label={DECOMP_LABELS.constraints} value={constraints} />
          <DecompCell label={DECOMP_LABELS.section} value={sectionValue} />
          <DecompCell label={DECOMP_LABELS.page} value={pageValue} />
        </div>
      )}
    </li>
  );
}

export function StatusBanner({
  notDecomposed,
  text,
}: {
  notDecomposed: number;
  text: string;
}) {
  const hasWarn = notDecomposed > 0;
  const cls = hasWarn ? STATUS_BANNER_WARN_CLS : STATUS_BANNER_OK_CLS;
  const Icon = hasWarn ? AlertTriangle : CheckCircle2;
  return (
    <div className={`flex items-start gap-2 rounded-lg border p-3 text-[12px] ${cls}`}>
      <Icon className="mt-0.5 size-4 flex-none" />
      <div className="min-w-0 break-words">{text}</div>
    </div>
  );
}

export interface RequirementDrawerHeaderProps {
  documentTitle?: string;
  documentType?: string;
  frameworkLabel?: string;
  backendSummary?: string;
  total: number;
  decomposedPct: number;
  decomposedTone: string;
  statusKind: RequirementStatusKind;
}

export function RequirementDrawerHeader({
  documentTitle,
  documentType,
  frameworkLabel,
  backendSummary,
  total,
  decomposedPct,
  decomposedTone,
  statusKind,
}: RequirementDrawerHeaderProps) {
  const decomposedLabel =
    total > 0 ? `${Math.round(decomposedPct)}%` : EMPTY_PLACEHOLDER;
  const frameworkText = plainText(frameworkLabel || REQUIREMENT_DRAWER_COPY.frameworkFallback);

  return (
    <header className="sticky top-0 z-10 flex flex-col gap-3 border-b border-border bg-card p-4 pr-14 sm:p-6 sm:pr-16 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className={HEADER_ICON_BOX_CLS}>
          <FileCheck className={HEADER_ICON_CLS} />
        </div>
        <div className="min-w-0 flex flex-col gap-1.5">
          <div className="text-lg font-bold text-foreground">
            {REQUIREMENT_DRAWER_COPY.headerTitle}
          </div>
          <p className="text-xs text-muted-foreground max-w-xl break-words">
            {REQUIREMENT_DRAWER_COPY.headerBlurb}
          </p>
          {backendSummary && (
            <p className="text-[11px] text-muted-foreground max-w-xl break-words">
              {plainText(backendSummary)}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            {documentTitle && (
              <span className={META_CHIP_FOREGROUND_CLS}>
                {plainText(documentTitle)}
              </span>
            )}
            {documentType && <DocTypeBadge documentType={documentType} />}
            <span className={META_CHIP_CLS}>Framework · {frameworkText}</span>
            <span className={META_CHIP_CLS}>{REQUIREMENT_DRAWER_COPY.layerLabel}</span>
            <span className={META_CHIP_CLS}>
              {REQUIREMENT_DRAWER_COPY.extractedLabel} · {total}
            </span>
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${REQUIREMENT_STATUS_PILL_CLS[statusKind]}`}
            >
              <span
                className={`size-1.5 rounded-full ${REQUIREMENT_STATUS_DOT_CLS[statusKind]}`}
              />
              {REQUIREMENT_STATUS_LABEL[statusKind]}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-start gap-4">
        <div className="flex flex-col items-end">
          <span className="text-[11px] text-muted-foreground">
            {REQUIREMENT_DRAWER_COPY.decomposedLabel}
          </span>
          <span className={`text-3xl font-bold tabular-nums ${decomposedTone}`}>
            {decomposedLabel}
          </span>
        </div>
      </div>
    </header>
  );
}
